import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DatePicker from "@/components/DatePicker";

/**
 * Renders a list of admin-defined custom fields onto a form.
 * `values` is a dict keyed by field.id. `onChange(id, v)` updates a single field.
 */
export default function CustomFieldsRenderer({ fields = [], values = {}, onChange }) {
  if (!fields || fields.length === 0) return null;

  return (
    <div className="space-y-5" data-testid="custom-fields-section">
      {fields.map((f) => {
        const v = values[f.id];
        const labelEl = (
          <Label className="text-forest">
            {f.label}
            {f.required && <span className="text-clay ml-1">*</span>}
            {!f.required && <span className="text-sage font-normal"> (optional)</span>}
          </Label>
        );

        if (f.type === "info") {
          return (
            <div
              key={f.id}
              className="bg-cream/60 border border-line rounded-sm p-4"
              data-testid={`info-box-${f.id}`}
            >
              <div className="text-xs uppercase tracking-[0.15em] text-clay font-semibold mb-2">
                {f.label}
              </div>
              <p className="text-sm text-forest whitespace-pre-line">{f.content}</p>
            </div>
          );
        }

        if (f.type === "textarea") {
          return (
            <div key={f.id}>
              {labelEl}
              <Textarea
                value={v || ""}
                onChange={(e) => onChange(f.id, e.target.value)}
                rows={3}
                className="mt-1.5 border-line input-clean resize-none"
                data-testid={`custom-${f.id}`}
              />
              {f.help && <p className="text-xs text-sage mt-1.5">{f.help}</p>}
            </div>
          );
        }

        if (f.type === "dropdown") {
          return (
            <div key={f.id}>
              {labelEl}
              <Select value={v || ""} onValueChange={(val) => onChange(f.id, val)}>
                <SelectTrigger className="mt-1.5 h-11 border-line" data-testid={`custom-${f.id}`}>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {(f.options || []).map((opt) => (
                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {f.help && <p className="text-xs text-sage mt-1.5">{f.help}</p>}
            </div>
          );
        }

        if (f.type === "yes_no") {
          return (
            <div
              key={f.id}
              className="flex items-center justify-between bg-cream border border-line rounded-sm px-4 py-3"
            >
              {labelEl}
              <Switch
                checked={!!v}
                onCheckedChange={(val) => onChange(f.id, val)}
                data-testid={`custom-${f.id}`}
              />
            </div>
          );
        }

        if (f.type === "date") {
          return (
            <div key={f.id}>
              {labelEl}
              <div className="mt-1.5">
                <DatePicker
                  value={v || ""}
                  onChange={(val) => onChange(f.id, val)}
                  testId={`custom-${f.id}`}
                />
              </div>
              {f.help && <p className="text-xs text-sage mt-1.5">{f.help}</p>}
            </div>
          );
        }

        // text or number
        return (
          <div key={f.id}>
            {labelEl}
            <Input
              type={f.type === "number" ? "number" : "text"}
              value={v ?? ""}
              onChange={(e) => onChange(f.id, e.target.value)}
              className="mt-1.5 h-11 border-line input-clean"
              data-testid={`custom-${f.id}`}
            />
            {f.help && <p className="text-xs text-sage mt-1.5">{f.help}</p>}
          </div>
        );
      })}
    </div>
  );
}
